<template>
    <div class="drawer-backdrop" @click.self="handleBackdropClick">
    <transition name="slide-in-right">
        <div ref="drawerRef" class="employee-drawer">
            <!-- Header -->
            <div class="drawer-header">
                <div class="header-content">
                    <h2 class="employee-name">{{ employeeName }}</h2>
                    <p class="employee-email">
                        <span class="employee-email__value">{{ primaryEmail }}</span>
                    </p>
                    <!-- Company-assigned/work email, when it differs from the
                         employee's primary email — shown so an admin can tell the
                         company-owned address apart from a personal one. -->
                    <p v-if="companyEmail && !primaryIsCompanyEmail" class="employee-email employee-email--company">
                        <span class="email-tag">Company</span>
                        <span class="employee-email__value">{{ companyEmail }}</span>
                    </p>
                    <!-- What Send invite did, said here rather than in a toast (D29). -->
                    <p v-if="inviteStatus" class="invite-note" :class="{ 'invite-note--error': inviteStatus.error }">
                        {{ inviteStatus.text }}
                    </p>
                </div>
                <div class="header-actions">
                    <PrimeButton
                        v-if="canSendInvite"
                        :label="inviteState === 'invite_stale' ? 'Resend' : 'Send invite'"
                        size="small"
                        :severity="inviteState === 'invite_stale' ? 'primary' : 'secondary'"
                        :outlined="inviteState !== 'invite_stale'"
                        :loading="isSendingInvite"
                        @click="sendInvite"
                    />
                    <PrimeButton
                        v-if="employee && canEditEmployee"
                        label="Impersonate"
                        size="small"
                        severity="secondary"
                        outlined
                        :disabled="!impersonationEligible"
                        v-tooltip.left="impersonationEligible
                            ? `Sign in as this employee to view and edit their identity vault: name, email, address, and other personal info.`
                            : `Only licensed members and business employees can be impersonated.`"
                        :loading="isImpersonating"
                        @click="impersonate"
                    >
                        <template #icon>
                            <PhUserSwitch :size="16" />
                        </template>
                    </PrimeButton>
                    <button type="button" class="close-btn" @click="handleBackdropClick">
                        <PhX :size="20" />
                    </button>
                </div>
            </div>

            <div v-if="isLoading" class="drawer-loading">
                <PrimeSkeleton width="100%" height="24px" class="mb-4" />
                <PrimeSkeleton width="80%" height="60px" class="mb-6" />
                <PrimeSkeleton width="100%" height="120px" class="mb-4" />
                <PrimeSkeleton width="100%" height="80px" />
            </div>

            <div v-else-if="employee" class="drawer-body">
                <!-- Privacy Risk Score -->
                <section class="drawer-section">
                    <h3 class="section-title">PRIVACY RISK SCORE</h3>
                    <div class="risk-score-row">
                        <span class="risk-value">{{ hasRiskScore ? `${employee.risk_score} /100` : '—' }}</span>
                        <div class="risk-bar">
                            <div
                                class="risk-bar-fill"
                                :style="{ width: `${hasRiskScore ? employee.risk_score : 0}%`, backgroundColor: riskColor }"
                            />
                        </div>
                    </div>
                    <p class="risk-label" :class="hasRiskScore ? getRiskClass(employee.risk_score) : ''">
                        {{ hasRiskScore ? getRiskLabel(employee.risk_score) : riskUnavailableLabel }}
                    </p>
                </section>

                <!-- LPOA: the grid carries no per-row badge, so the state lives here. -->
                <section class="drawer-section">
                    <h3 class="section-title">LPOA</h3>
                    <p class="lpoa-status" :class="{ 'lpoa-status--unsigned': employee.lpoa_signed === false }">
                        {{ lpoaLabel }}
                    </p>
                </section>

                <!-- Role & Department -->
                <section class="drawer-section">
                    <h3 class="section-title">ROLE & DEPARTMENT</h3>
                    <div class="form-group">
                        <label class="form-label">Roles</label>
                        <!-- Roles are read-only without role-management write, and always
                             for the viewer's own row (D22): the held roles are shown, never
                             hidden, so the lock says "you can't change this", not "you
                             can't see this". -->
                        <div v-if="!canEditRoles" class="restricted-field">
                            <PhLock :size="14" />
                            <span>{{ heldRoleSummary }}</span>
                        </div>
                        <PrimeMultiSelect
                            v-else
                            v-model="selectedRoles"
                            :options="businessRoles"
                            placeholder="Select roles"
                            class="roles-select w-full"
                            :maxSelectedLabels="3"
                            appendTo="body"
                            :pt="{ listContainer: { style: 'max-height: 200px; overflow-y: auto' } }"
                        />
                        <span v-if="saveErrors.roles" class="form-error">{{ saveErrors.roles }}</span>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Department</label>
                        <!-- Read-only roles get a disabled selector: scheduleAutosave
                             drops their edits, so an interactive control would show a
                             department change that is never persisted. -->
                        <PrimeSelect
                            v-model="selectedDepartment"
                            :options="departments"
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select department"
                            class="department-select w-full"
                            :disabled="!canEditEmployee"
                        >
                            <template #value="slotProps">
                                <div v-if="slotProps.value" class="dept-value">
                                    <span class="dept-indicator" :style="{ backgroundColor: getDepartmentColor(slotProps.value) }" />
                                    {{ getDepartmentName(slotProps.value) }}
                                </div>
                                <span v-else class="placeholder-text">Select department</span>
                            </template>
                            <template #option="slotProps">
                                <div class="dept-option">
                                    <span class="dept-indicator" :style="{ backgroundColor: slotProps.option.color }" />
                                    {{ slotProps.option.name }}
                                </div>
                            </template>
                        </PrimeSelect>
                        <span v-if="saveErrors.department" class="form-error">{{ saveErrors.department }}</span>
                    </div>
                </section>

                <!-- License -->
                <section class="drawer-section">
                    <h3 class="section-title">LICENSE</h3>
                    <div class="form-group">
                        <label class="form-label">License type</label>
                        <!-- Same write gate as the Department selector above: without
                             it, a read-only user's pick lingers as fake unsaved state.
                             The panel re-reads the seat counts as it opens: seats can be
                             bought on the billing page or in another tab between the
                             drawer opening and this edit, and without it the dropdown
                             keeps saying "Out of stock" for a SKU that now has seats. -->
                        <PrimeSelect
                            v-model="selectedLicense"
                            :options="licenseOptions"
                            optionLabel="displayName"
                            optionValue="slug"
                            optionDisabled="outOfStock"
                            placeholder="Select license"
                            class="license-select w-full"
                            :disabled="!canEditEmployee"
                            @show="refreshSeatCounts"
                            @change="onLicenseChange"
                        >
                            <!-- PB-136: an employee holds one license, so the chosen tier
                                 reads as plain bold text. A colored pill in a closed
                                 single-select was the "Fisher-Price" complaint, and it
                                 carried nothing the tier's name does not already say. -->
                            <template #value>
                                <span class="license-value">{{ selectedLicenseName }}</span>
                            </template>
                            <template #option="slotProps">
                                <div
                                    class="license-option"
                                    :class="{ 'license-option--out': slotProps.option.outOfStock }"
                                >
                                    <LicenseBadge :license="slotProps.option.slug" size="sm" />
                                    <span
                                        v-if="slotProps.option.slug && !isPartnerCompany"
                                        class="license-avail"
                                        :class="{ 'license-avail--out': slotProps.option.outOfStock }"
                                    >
                                        {{ slotProps.option.available }} available
                                    </span>
                                </div>
                            </template>
                        </PrimeSelect>
                        <span v-if="saveErrors.license" class="form-error">{{ saveErrors.license }}</span>

                        <!-- The purchase happens in another tab, which this drawer cannot
                             observe: offer the re-read rather than leaving the tier stuck
                             at "Out of stock" after seats were bought. -->
                        <button
                            v-if="awaitingSeats"
                            type="button"
                            class="refresh-seats"
                            @click="refreshSeats"
                        >
                            Bought licenses in the other tab? Refresh licenses
                        </button>
                    </div>

                    <!-- The employee bought their own consumer plan. Only the fact is
                         shown (D9): the plan's name, renewal date and price are the
                         employee's private business, not every employee-read holder's. -->
                    <div v-if="hasPersonalPlan" class="personal-license">
                        <PhInfo :size="16" class="personal-license__icon" />
                        <p class="personal-license__body">Has their own Privacy Bee plan. Review before assigning a license.</p>
                    </div>
                </section>

                <!-- Family Members (only when the company has family viewing enabled) -->
                <section v-if="canViewFamily" class="drawer-section">
                    <div class="section-row">
                        <h3 class="section-title">FAMILY MEMBERS</h3>
                        <span
                            class="consent-chip"
                            :class="employee.minor_consent_signed ? 'consent-chip--signed' : 'consent-chip--unsigned'"
                        >
                            <PhCheckCircle v-if="employee.minor_consent_signed" :size="12" weight="fill" />
                            <PhWarningCircle v-else :size="12" weight="fill" />
                            {{ employee.minor_consent_signed ? 'Consent signed' : 'Consent needed' }}
                        </span>
                    </div>
                    <div v-if="familyLoading" class="family-loading">
                        <PrimeSkeleton width="100%" height="44px" v-for="n in 2" :key="n" class="mb-2" />
                    </div>
                    <div v-else-if="familyMembers.length > 0" class="family-list">
                        <div v-for="member in familyMembers" :key="member.id" class="family-card">
                            <div class="family-card__head">
                                <div class="family-card__info">
                                    <span class="family-card__name">{{ personDisplayName(member) }}</span>
                                    <span class="family-card__meta">{{ familyMetaLine(member) }}</span>
                                </div>
                                <!-- What the company holds on THIS person, and nothing
                                     else: the family list carries no tally of licenses,
                                     because a pool figure beside a relative's name reads
                                     as an allowance the company was sold (PB-012). -->
                                <LicenseBadge v-if="member.license" :license="member.license" size="sm" />
                                <span v-else class="family-card__unlicensed">No company license</span>
                                <span v-if="member.is_minor" class="family-card__tag">Minor</span>
                            </div>

                            <div v-if="canEditEmployee" class="family-card__actions">
                                <!-- A family member with no company license is refused by
                                     the API (hasCompanyOwnedLicense is the only arm that
                                     opens a non-employee's record to a company admin), so
                                     the row says so instead of letting them find out.
                                     Said as TEXT, not as a tooltip on a disabled button: a
                                     disabled native button takes no keyboard focus and fires
                                     no hover, so a reason hung on one reaches nobody who
                                     needs it. Either the control is here, or the
                                     requirement is. -->
                                <button
                                    v-if="canImpersonateRelative(member)"
                                    type="button"
                                    class="family-link"
                                    :disabled="impersonatingRelativeId === member.id || familyMutating"
                                    v-tooltip.top="`Sign in as this family member to view and edit their identity vault.`"
                                    @click="impersonateRelative(member)"
                                >
                                    View account
                                </button>
                                <span v-else class="family-card__requirement">
                                    Assign a company license to open this family member's account.
                                </span>
                                <!-- Every family control is locked while either mutation is
                                     out: the two panels hide each other, so an open panel is
                                     not proof that the other flow is idle. -->
                                <button
                                    type="button"
                                    class="family-link"
                                    :disabled="familyMutating"
                                    @click="openAssign(member)"
                                >
                                    {{ member.license ? 'Change license' : 'Assign license' }}
                                </button>
                                <button
                                    type="button"
                                    class="family-link family-link--danger"
                                    :disabled="familyMutating"
                                    @click="openRemove(member)"
                                >
                                    Remove
                                </button>
                            </div>

                            <!-- Assign from the pool the company already paid for. No
                                 price, no quantity, no payment step: the one money path
                                 is the ordinary business checkout (PB-013). -->
                            <div v-if="assignForId === member.id" class="family-panel">
                                <template v-if="assignablePool.length > 0">
                                    <p class="family-panel__lede">
                                        Give {{ personDisplayName(member) }} one of the licenses your company
                                        already owns. Nothing is bought here.
                                    </p>
                                    <div class="family-panel__tiers">
                                        <button
                                            v-for="tier in assignablePool"
                                            :key="tier.slug"
                                            type="button"
                                            class="family-tier"
                                            :disabled="familyMutating"
                                            @click="assignFamilyLicense(member, tier.slug)"
                                        >
                                            {{ tier.displayName }}
                                        </button>
                                    </div>
                                </template>
                                <p v-else class="family-panel__lede">
                                    Your company has no licenses available to assign.
                                </p>
                                <p v-if="assignError" class="family-panel__error">{{ assignError }}</p>
                                <div class="family-panel__actions">
                                    <button
                                        v-if="canBuySeats && (assignablePool.length === 0 || offerPurchase)"
                                        type="button"
                                        class="family-link"
                                        @click="buyCompanyLicenses"
                                    >
                                        Buy company licenses
                                    </button>
                                    <span
                                        v-else-if="assignablePool.length === 0 || offerPurchase"
                                        class="family-panel__aside"
                                    >
                                        Ask someone with billing access to buy more.
                                    </span>
                                    <button type="button" class="family-link" :disabled="familyMutating" @click="closeAssign">Cancel</button>
                                </div>
                            </div>

                            <!-- Removing a relative is not deleting a person, and an admin
                                 should not have to guess the difference: the whole of what
                                 it does is said before it is done (PB-007). -->
                            <div v-if="removeForId === member.id" class="family-panel">
                                <p class="family-panel__lede">
                                    Remove {{ personDisplayName(member) }} from {{ employeeName }}'s family. Their
                                    Privacy Bee account, their history and their vault all stay exactly as they
                                    are, and nothing is deleted. The license your company holds on them goes back to
                                    your pool right away, and they can be added again later or buy a plan of their
                                    own.
                                </p>
                                <p v-if="removeError" class="family-panel__error">{{ removeError }}</p>
                                <div class="family-panel__actions">
                                    <button
                                        type="button"
                                        class="family-link family-link--danger"
                                        :disabled="familyMutating"
                                        @click="removeFamilyMember(member)"
                                    >
                                        Remove
                                    </button>
                                    <button type="button" class="family-link" :disabled="familyMutating" @click="closeRemove">Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p v-else-if="!familyLoadFailed" class="family-hint">
                        No family members yet. To add a child, a parent or guardian signs a
                        short consent form first.
                    </p>
                    <!-- A read that did not land is not an empty family: whatever is above
                         is the last known list, and this says so rather than letting it
                         read as current. -->
                    <p v-if="familyLoadFailed" class="family-note">
                        This employee's family could not be read just now, so this list may be
                        out of date. Close and reopen the drawer to try again.
                    </p>
                    <div v-if="canEditEmployee" class="family-actions">
                        <PrimeButton
                            label="Add family member"
                            size="small"
                            severity="secondary"
                            outlined
                            @click="openAddFamilyMember"
                        >
                            <template #icon>
                                <PhUsersThree :size="16" />
                            </template>
                        </PrimeButton>
                        <PrimeButton
                            label="Add minor child"
                            size="small"
                            severity="secondary"
                            outlined
                            @click="openAddChild"
                        >
                            <template #icon>
                                <PhBaby :size="16" />
                            </template>
                        </PrimeButton>
                    </div>
                </section>

                <!-- Recent Exposure Reports -->
                <section v-if="canViewReports" class="drawer-section">
                    <h3 class="section-title">RECENT EXPOSURE REPORTS</h3>
                    <div v-if="reportsLoading" class="reports-loading">
                        <PrimeSkeleton width="100%" height="48px" v-for="n in 3" :key="n" class="mb-2" />
                    </div>
                    <div v-else-if="recentReports.length === 0" class="reports-empty">
                        <p class="text-sm text-zinc-500">No reports generated yet</p>
                    </div>
                    <div v-else class="reports-list">
                        <div
                            v-for="report in recentReports"
                            :key="report.id"
                            class="report-card"
                        >
                            <div class="report-info">
                                <span class="report-name">{{ report.name }}</span>
                                <span class="report-date">{{ formatDate(report.created_at) }}</span>
                            </div>
                            <button
                                type="button"
                                class="download-btn"
                                :disabled="downloadingReportId === report.id"
                                :aria-label="downloadingReportId === report.id ? 'Opening report' : 'Download report'"
                                :title="downloadingReportId === report.id ? 'Opening…' : 'Download'"
                                @click="downloadReport(report)"
                            >
                                <PhDownloadSimple :size="22" />
                            </button>
                        </div>
                    </div>
                </section>

                <!-- Remove Employee -->
                <div v-if="canEditEmployee" class="drawer-footer">
                    <button type="button" class="remove-link" @click="confirmRemoveEmployee">
                        Remove employee from organization
                    </button>
                </div>
            </div>
        </div>
    </transition>
    </div>
</template>

<script setup lang="ts">
import {
    PhBaby,
    PhCheckCircle,
    PhDownloadSimple,
    PhInfo,
    PhLock,
    PhUserSwitch,
    PhUsersThree,
    PhWarningCircle,
    PhX,
} from '@phosphor-icons/vue';
import { formatDate } from '@privacy-bee/format/date';
import { personDisplayName } from '@privacy-bee/format/text';
import { businessService } from '~/services/business-service';
import { useAuthStore } from '~/stores/auth';
import { useBusinessStore } from '~/stores/business';
import { canImpersonateTarget } from '~/stores/person';
import LicenseBadge from '~/components/business/shared/LicenseBadge.vue';
import type { Department, Employee, EmployeeFamilyMember, License, Report } from '~/types/business';
import { NO_LICENSE_SLUG, licenseSlug, licenseTierName } from '~/types/licenses/tiers';
import { checkoutBusinessUrl } from '~/utils/cross-app-urls';
import { ApiError, memberFacingMessage } from '~/utils/errors';
import { isDemoMode } from '~/services/demo/mode';
import { ModalNames } from '~/types/modals';
import { getDialogRef } from '~/composables/dynamic-dialog';
import type { DynamicDialogInstance } from 'primevue/dynamicdialogoptions';

const dialogRef = getDialogRef(ModalNames.EMPLOYEE_DETAILS_DRAWER);
const businessStore = useBusinessStore();
const { showSuccess, showError } = useGlobalToast();

// `data` is required for this modal (types/modals.ts), so only the injected
// dialogRef itself needs guarding — one shape for every read below.
const companyId = computed(() => dialogRef.value?.data.companyId);
const employeeId = computed(() => dialogRef.value?.data.employeeId);
const companySlug = computed(() => dialogRef.value?.data.companySlug);

// Snapshot the opening company: dialogRef.data is frozen for this drawer
// instance's whole lifetime, so this is the company every fetch/save/nested
// dialog below is bound to, regardless of what businessStore.companyId does later.
const openingCompanyId = companyId.value;

const isLoading = ref(true);
const isSaving = ref(false);
const reportsLoading = ref(true);
const employee = ref<Employee | null>(null);
// The roster page loaded all three for this company before it opened the drawer
// and handed them over, so the drawer asks only for the one employee.
//
// `licenses` is a READ of the page's list, never a write. That list is two
// endpoints merged — `getLicenseInformation` layered over `getLicenses` — and it
// is what the availability cards above the roster render from. Writing a bare
// `getLicenses` response into it would strip the second layer and leave those
// cards showing counts for SKUs that are no longer even in the list. Fresh seat
// counts come from `refreshFromPage()` below, which asks the page to re-run both
// steps and keeps it the single writer.
//
// `unref`, not `.value`: PrimeVue's DynamicDialog keeps its instances in reactive
// component data, so a ref handed over in `data` arrives here already unwrapped
// (`.value` on the array is undefined, and every list read empty in production);
// a hand-built dialogRef still carries the ref. Both shapes read the same way.
const licenses = computed<License[]>(() => unref(dialogRef.value?.data.licenses) ?? []);
const departments = computed<Department[]>(() => unref(dialogRef.value?.data.departments) ?? []);
// Exclude PB-internal (privacy_bee guard) roles: saveChanges posts bare role
// names against the business guard, so offering one here would hit the ambiguous
// assignment path or attach a same-named business role.
const businessRoles = computed<string[]>(() =>
    (unref(dialogRef.value?.data.roles) ?? [])
        .filter((r) => r.name !== 'Employee' && r.guard_name !== 'privacy_bee')
        .map((r) => r.name),
);
const recentReports = ref<Report[]>([]);
const familyMembers = ref<EmployeeFamilyMember[]>([]);
const familyLoading = ref(false);
// The last family read did not land. Held apart from an empty list because the
// two are different facts and only one of them is "nobody is here".
const familyLoadFailed = ref(false);

const selectedLicense = ref('');
const selectedDepartment = ref<number | null>(null);
const selectedRoles = ref<string[]>([]);
const hasUpdatedDuringSession = ref(false);

type SaveField = 'license' | 'department' | 'roles';
// The API's reason for refusing the last autosave, shown beside the control that
// asked (D9). Cleared when the next save attempt starts.
const saveErrors = ref<Partial<Record<SaveField, string>>>({});

// Autosave: the drawer has no Save button — edits to role/department/license
// persist automatically. `isReady` gates the watcher so the initial population
// in fetchEmployee() doesn't fire a save; the debounce coalesces rapid edits
// (e.g. toggling several roles) into one request.
const isReady = ref(false);
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
// Handle to the in-flight autosave so a backdrop close awaits the same
// request instead of firing a duplicate write.
let savePromise: Promise<boolean> | null = null;

const drawerRef = ref<HTMLElement | null>(null);

// Every mutating drawer flow — the role picker, the department selector, Save
// and Remove employee — is one `write employees` grant, so it is one gate.
const canEditEmployee = computed(() => businessStore.hasPermission('employees', 'write'));

// Roles are the one field on a stricter gate (D22): employee-write alone let an
// HR user make themselves Admin. Role-management write is required, and nobody
// edits their own row — the signed-in identity is the same person id an
// employee row carries.
const canEditRoles = computed(() =>
    canEditEmployee.value
    && businessStore.hasPermission('role-management', 'write')
    && employee.value?.id !== useAuthStore().user?.identity?.id,
);

// The roles an employee holds beyond the base one, as the read-only view names
// them; "Employee" alone when there are none.
const heldRoleSummary = computed(() =>
    (employee.value?.roles ?? []).filter((r) => r && r !== 'Employee').join(', ') || 'Employee',
);

// Employee exposure reports require reporting access — the report pages
// themselves redirect without it, so gate the drawer's fetch + download UI the
// same way instead of exposing downloads to employees-only users. The store's
// 'read' check passes a user holding only `write reporting`, because the role
// editor stores read and write independently and write implies view.
const canViewReports = computed(() => businessStore.hasPermission('reporting', 'read'));

// Family Members section is gated on the company-level
// `can_view_employee_family` flag. When disabled, the entire section
// (consent chip + Add Child) is hidden so admins of companies without
// family management see no minor-related UI at all.
const canViewFamily = computed(() => !!businessStore.business?.can_view_employee_family);

const employeeName = computed(() => {
    if (!employee.value) return '';
    // `name` on the V2 payload is the full legal composition, middle name included, and the title is a label,
    // which reads first and last only (PB-138).
    return personDisplayName(employee.value) || 'Unknown employee';
});

const primaryEmail = computed(() => {
    const emp = employee.value;
    if (!emp) return '';
    const emails = emp.emails as Array<{ email: string; primary?: boolean }> | undefined;
    if (emails?.length) {
        const primary = emails.find((e) => e.primary);
        return (primary || emails[0])?.email || '';
    }
    return emp.email || '';
});

// The email the company assigned/owns for this employee (its own
// company_employee_emails row), resolved server-side on the detail payload.
// Empty when the company has no on-file email match for the person.
const companyEmail = computed(() => employee.value?.company_email || '');

// When the primary email IS the company-owned one, tag it inline rather than
// repeating the same address on a second line.
const primaryIsCompanyEmail = computed(
    () => !!companyEmail.value && companyEmail.value === primaryEmail.value,
);

// The employee owns a consumer plan of their own: the detail payload carries it
// as `personal_license`, null when none. Only its existence is read — the note
// deliberately names no plan, date or price (D9). Guard on `type` so an empty
// shell never shows the note.
const hasPersonalPlan = computed(() => !!employee.value?.personal_license?.type);

// The employee's current license slug ('' for none; `fetchEmployee` resolves the
// payload to it), so re-saving the same SKU doesn't trip the gate when stock is 0.
const currentLicenseSlug = computed(() => employee.value?.license ?? '');

const hasChanges = computed(() => {
    if (!employee.value) return false;
    return (
        selectedLicense.value !== currentLicenseSlug.value ||
        selectedDepartment.value !== (employee.value.department_id ?? null) ||
        JSON.stringify([...selectedRoles.value].sort()) !== JSON.stringify([...(employee.value.roles || [])].filter((r) => r !== 'Employee').sort())
    );
});

const riskColor = computed(() => {
    const score = employee.value?.risk_score;
    if (score === undefined || score === null) return '#a1a1aa';
    if (score >= 75) return '#ef4444';
    if (score >= 40) return '#f59e0b';
    return '#22c55e';
});

// Unknown (the API did not say) claims nothing, rather than reading as unsigned.
const lpoaLabel = computed(() => {
    const signed = employee.value?.lpoa_signed;
    if (signed === true) return 'LPOA signed';
    if (signed === false) return 'LPOA not signed';
    return '—';
});

const hasRiskScore = computed(() => !!employee.value && riskState(employee.value) === 'score');

// Same wording as the roster cell: both ask utils/employee-risk.
const riskUnavailableLabel = computed(() => (employee.value ? riskPendingTooltip(employee.value) : ''));

// Regular companies (is_partner=false) buy seats up front: assignment can
// only consume an already-paid pool, so the dropdown disables out-of-stock
// SKUs and bounces the click to the purchase dialog. Channel-partner-owned
// companies (is_partner=true) provision on demand via the partner flow, so
// no gating is applied for them.
const isPartnerCompany = computed(() => !!businessStore.business?.is_partner);

// Only a financial-write admin is sent to buy seats; everyone else is left with
// the out-of-stock tier simply unavailable, as before.
const canBuySeats = computed(() => businessStore.hasPermission('financial', 'write'));

// True once this drawer has sent the admin to checkout in another tab. It is what
// puts the "Refresh seats" affordance on screen — the purchase lands somewhere
// this drawer cannot observe.
const awaitingSeats = ref(false);

/**
 * Re-read the company's seat counts the only way that keeps them consistent: ask
 * the roster page to re-run its own two-step license load. The dropdown is fed
 * from that page's list, so when it lands both the dropdown and the availability
 * cards move together.
 *
 * Soft-fail: a refresh that does not land leaves the counts already on screen,
 * which are the ones the reader was given.
 */
async function refreshFromPage(): Promise<boolean> {
    const onRefresh = dialogRef.value?.data.onRefresh;
    if (typeof onRefresh !== 'function') return false;
    try {
        await onRefresh();
        return true;
    } catch {
        // soft-fail: keep showing whatever's already loaded
        return false;
    }
}

/**
 * Opening the tier list, or coming back from checkout, only needs fresh seat
 * counts: the page re-reads its license information in place and the roster
 * is left alone, so the table behind the drawer does not repaint.
 * Falls back to the full refresh when the page did not hand over the narrower one.
 */
async function refreshSeatCounts(): Promise<boolean> {
    const onRefreshSeats = dialogRef.value?.data.onRefreshSeats;
    if (typeof onRefreshSeats !== 'function') return refreshFromPage();
    try {
        await onRefreshSeats();
        return true;
    } catch {
        return false;
    }
}

async function refreshSeats(): Promise<void> {
    // Only a refresh that landed retires the affordance — otherwise the reader is
    // left with stale counts and no way to ask again.
    if (await refreshSeatCounts()) awaitingSeats.value = false;
}

const NO_LICENSE_NAME = licenseTierName(NO_LICENSE_SLUG);

const licenseOptions = computed(() => {
    const options: Array<{
        slug: string;
        name: string;
        displayName: string;
        available: number;
        pool: number;
        outOfStock: boolean;
    }> = [
        // The empty slug is this picker's "no license"; the service sends it as `NO_LICENSE_SLUG`.
        { slug: '', name: NO_LICENSE_NAME, displayName: NO_LICENSE_NAME, available: 0, pool: 0, outOfStock: false },
    ];
    licenses.value.forEach((license) => {
        const available = license.available ?? 0;
        const isCurrent = license.slug === currentLicenseSlug.value;
        // Out-of-stock only matters for regular companies. Partner companies
        // can provision freely, so this flag is permanently false for them.
        const outOfStock = !isPartnerCompany.value && available <= 0 && !isCurrent;
        // The tier-map name, or the catalogue's own for a license outside the
        // ladder (a partner SKU): `displayName` is the Select's optionLabel, so it
        // is the accessible name and the filter text behind every LicenseBadge in
        // the list.
        const tierName = licenseTierName(license);
        // A partner company provisions on demand (D23): no count, and never "0 available".
        const displayName = isPartnerCompany.value ? tierName : `${tierName} (${available} available)`;
        options.push({
            slug: license.slug,
            name: tierName,
            displayName,
            available,
            pool: license.pool ?? 0,
            outOfStock,
        });
    });
    return options;
});

// The tier name for the closed select: the bare name, with no "(N available)"
// tail — the count belongs to the open list, where a tier is being chosen.
// Resolved by slug OR displayName for the same reason `saveChanges` does it:
// PrimeVue 4 does not reliably bind the empty-string `optionValue`, so v-model
// can be holding a displayName. `licenseTierName` would pass that through
// verbatim, leaking "Pro Business (21 available)" into the closed box.
const selectedLicenseName = computed(() => {
    const picked = licenseOptions.value.find(
        (opt) => opt.slug === selectedLicense.value || opt.displayName === selectedLicense.value
    );
    return picked?.name ?? licenseTierName(selectedLicense.value);
});

function getDepartmentColor(id: number | null) {
    if (!id) return '#a1a1aa';
    const dept = departments.value.find((d) => d.id === id);
    return dept?.color || '#a1a1aa';
}

function getDepartmentName(id: number | null) {
    if (!id) return '';
    const dept = departments.value.find((d) => d.id === id);
    return dept?.name || '';
}

function getRiskClass(score: number | undefined): string {
    if (score === undefined) return '';
    if (score >= 75) return 'risk-high';
    if (score >= 40) return 'risk-medium';
    return 'risk-low';
}

function getRiskLabel(score: number | undefined) {
    if (score === undefined) return 'Unknown';
    if ((score as number) >= 75) return 'High risk. This employee is not secure.';
    if ((score as number) >= 40) return 'Medium risk. This employee could be more secure.';
    return 'Low risk. This employee is secure.';
}

// Intercept license picks: regular companies can only assign from already-
// paid stock. If the user picks an SKU with no inventory, revert the
// selection and send them to checkout so they can buy more before
// assigning. Partner companies skip this branch (provision on demand).
function onLicenseChange(event: { value: string | null }) {
    const value = event?.value ?? '';
    // Match on slug OR displayName — PrimeVue may bind either depending on
    // whether the underlying option's optionValue is non-empty.
    const picked = licenseOptions.value.find(
        (opt) => opt.slug === value || opt.displayName === value
    );
    if (picked?.outOfStock) {
        const previous = (employee.value?.license as string | undefined) ?? '';
        selectedLicense.value = previous;
        // Seats are bought in the checkout app, which is a page rather than a dialog.
        // Open it in a NEW TAB: navigating in place would close this drawer and throw
        // away edits that have not autosaved yet. `awaitingSeats` then offers a
        // refresh, so a seat bought in that tab becomes assignable here without
        // reopening the drawer.
        // Same refusal the other three handoffs give: the demo has no auth token, so
        // checkout would bounce the tab to the auth app. Saying nothing would read as
        // the selection being ignored.
        if (isDemoMode()) {
            showError('Not in the demo', 'Buying licenses opens the real checkout, which the demo cannot reach.');
            return;
        }

        if (canBuySeats.value && companyId.value) {
            // The tier they just tried to assign is the tier they need more of, so the
            // checkout opens on that plan's summary rather than asking them to pick it again.
            window.open(
                checkoutBusinessUrl(companyId.value, `/business/${companySlug.value ?? ''}/employees`, { plan: picked.slug }),
                '_blank',
                'noopener',
            );
            awaitingSeats.value = true;
        }
    }
}

async function fetchEmployee() {
    if (!companyId.value || !employeeId.value) return;
    isLoading.value = true;
    try {
        const empData = await businessService.getEmployee(companyId.value, employeeId.value);
        // The detail payload nests the seat resource under `license`; the drawer holds its slug.
        const licenseValue = licenseSlug(empData.license) || undefined;
        // V3 sends `last_login`; the roster maps it to `last_login_at` the same way.
        const lastLogin = empData.last_login_at ?? (empData as { last_login?: string | null }).last_login ?? undefined;
        employee.value = { ...empData, license: licenseValue, last_login_at: lastLogin };
        selectedLicense.value = licenseValue || '';
        selectedDepartment.value = empData.department_id ? Number(empData.department_id) : null;
        selectedRoles.value = (empData.roles || []).filter((r: string) => r != null && r !== 'Employee');
        // Enable autosave only after the initial values are seeded so the
        // population above doesn't trigger a spurious save.
        await nextTick();
        isReady.value = true;
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to load employee'));
        closeDrawer();
    } finally {
        isLoading.value = false;
    }
}

/**
 * Only the newest read may write the family list. Four callers reach this — the
 * mount, the add-family and add-child dialogs closing, and the re-read a removal
 * makes once its DELETE has succeeded — and two of them can be out at once: an
 * add finishing during a slow removal starts a read that the server answers
 * BEFORE it has processed the DELETE, so that answer still carries the relative
 * being let go. Landing last, it would put them back on the list holding a
 * license the company no longer holds on them. Same guard, same reason, as
 * `latestLicenseInformationRequestId` on the employees page.
 *
 * Answers whether THIS call read the list, which is what a caller asking for
 * proof needs — a superseded read answers false even though the list is current,
 * because the proof has to come from a read the caller made itself.
 */
let latestFamilyRequestId = 0;

async function fetchFamily(): Promise<boolean> {
    const requestId = ++latestFamilyRequestId;
    // Skip the request entirely when the company doesn't have family
    // viewing enabled; the endpoint would 403 and we'd just throw it away.
    // The bump above still stands: this is a definite answer about what there is
    // to show, so a read already in flight no longer owns the list.
    if (!companyId.value || !employeeId.value || !canViewFamily.value) {
        familyMembers.value = [];
        familyLoadFailed.value = false;
        return true;
    }
    familyLoading.value = true;
    try {
        const members = await businessService.getEmployeeFamily(companyId.value, employeeId.value);
        if (requestId !== latestFamilyRequestId) return false;
        familyMembers.value = members;
        familyLoadFailed.value = false;
        return true;
    } catch (error) {
        console.error('Failed to load family members:', error);
        if (requestId !== latestFamilyRequestId) return false;
        // A read that did not land is not an empty family. Clearing the list here
        // turned a transient GET into "No family members yet", and — because a
        // remove re-reads once its DELETE has succeeded — it turned letting one
        // relative go into the whole family having gone. The list keeps whatever
        // was last known and the note below it says the read failed.
        familyLoadFailed.value = true;
        return false;
    } finally {
        // A superseded read dropping the skeleton would flash the list while the
        // newest one is still out.
        if (requestId === latestFamilyRequestId) familyLoading.value = false;
    }
}

function familyMetaLine(member: EmployeeFamilyMember): string {
    // Show email for adult family members (real, admin-meaningful). Skip it
    // for minors — the backend generates a synthetic
    // `<guardian>.child-<id>` placeholder that isn't useful to display.
    const parts: string[] = [];
    if (typeof member.age === 'number') parts.push(`${member.age} yr${member.age === 1 ? '' : 's'}`);
    if (!member.is_minor && member.primary_email) parts.push(member.primary_email);
    return parts.join(' · ');
}

// ── A family member, managed from here (PB-030/PB-055) ───────────────────────
// Impersonation is still a door into a relative's record, but it is no longer
// the only one: view, license and remove all live on this drawer, so an admin
// never has to sign in as somebody to administer them.

// Which row has its assign panel or its remove confirmation open, and which row
// has a request out. One of each at a time: these panels push the rows below
// them down, and two open confirmations in a 480px drawer is a misclick.
const assignForId = ref<number | null>(null);
const removeForId = ref<number | null>(null);
const assigningRelativeId = ref<number | null>(null);
const removingRelativeId = ref<number | null>(null);
const impersonatingRelativeId = ref<number | null>(null);
const assignError = ref('');
const removeError = ref('');
// Set when the SERVER says the pool is empty — another admin took the last
// license after this drawer read its counts. The row then offers checkout even
// though its own figures said one was free.
const offerPurchase = ref(false);

/**
 * A family mutation is out. One at a time, across every row and both flows:
 * assign and remove move licenses in and out of the same company pool, so a PUT
 * racing a DELETE leaves the row, the pool counts and the toast disagreeing
 * about who the company is paying for. The two panels also hide each other
 * (`openAssign` clears `removeForId`), so without this the admin can open the
 * other flow over a request they can no longer see.
 */
const familyMutating = computed(() => assigningRelativeId.value !== null || removingRelativeId.value !== null);

/**
 * The words an admin reads when a family action is refused: ours, chosen here,
 * keyed on the envelope's machine-readable `reason` and on nothing else.
 *
 * `ApiError.message` is not copy for a customer's admin. For a transport failure
 * it is ofetch naming the HTTP method and the whole API URL; for a refusal this
 * build does not know it is whatever the API happened to say, which is not a
 * contract ({@see memberFacingMessage}). A reason we have words for gets them; a
 * reason we do not gets the authored fallback — which is every other refusal
 * these two endpoints can give: the 403 for a company without family viewing,
 * the 404 for a relative who is not this employee's, and the 422 the catalogue
 * lookup raises for a slug this company does not carry. None of them codes
 * itself, and a business route's `$request->validate` answers Laravel's plain
 * field map rather than the v3 `validation_failed` body (only staff routes are
 * rendered through `V3ErrorBody::invalid`), so it lands here too.
 */
const ASSIGN_REFUSALS: Record<string, string> = {
    // PB-013's one money path. `offerPurchase` puts business checkout under this.
    no_license_available: 'Your company has no licenses left to assign. Buy company licenses, then assign again.',
    relative_already_licensed: 'This family member already has a Privacy Bee plan of their own. A company license cannot be added on top of it.',
    // A 404 from `holdForFamilyLicenseWrite`: the employment ended between this
    // drawer opening and the click, and the whole claim rolled back.
    employee_not_found: 'This employee is no longer on your employee list, so their family cannot be changed. No license was assigned.',
};

const REMOVE_REFUSALS: Record<string, string> = {
    // Their license is theirs AS an employee; offboarding is what releases it.
    relative_is_employee: 'This family member is one of your employees. Remove them from your employee list instead.',
    // The same 404, and the reason it must never read as "already removed": the
    // transaction rolled back whole, so the license is still theirs.
    employee_not_found: 'This employee is no longer on your employee list, so their family cannot be changed. Nothing was removed.',
};

const refusalWords = (error: unknown, words: Record<string, string>, fallback: string): string =>
    (error instanceof ApiError && error.reason !== null ? words[error.reason] : undefined) ?? fallback;

/**
 * The licenses the company has paid for and not yet handed out: the only thing a
 * family member can be put in (PB-012). There is no family SKU and no
 * per-employee allowance, so this is the employee list minus "No license" — a
 * relative is released by Remove, not by a tier.
 *
 * No partner carve-out, unlike the employee's own tier list above. D23 — a
 * partner company provisions on demand — is an EMPLOYEE decision, and the family
 * endpoint passes `fromPoolOnly: true` (PB-061): `assignLicense` refuses an empty
 * pool `whatever kind of company this is`, because a relative's assignment that
 * provisioned would create a license outside the one money path (PB-013). So an
 * empty pool is an empty pool here, and offering a partner-owned company a tier
 * it has none of would offer a click the server is certain to refuse.
 */
const assignablePool = computed(() =>
    licenseOptions.value.filter((opt) => !!opt.slug && opt.available > 0),
);

function openAssign(member: EmployeeFamilyMember) {
    if (familyMutating.value) return;
    removeForId.value = null;
    removeError.value = '';
    assignError.value = '';
    offerPurchase.value = false;
    assignForId.value = member.id;
    // The panel names counts, and a license can be bought or claimed in another
    // tab between the drawer opening and this click — the same re-read the
    // employee's own tier list does as it opens.
    void refreshSeatCounts();
}

function closeAssign() {
    // Not while its own request is out: the success path clears `assignForId`
    // itself, so the only thing dismissing early could do is hide the refusal
    // that is about to land.
    if (familyMutating.value) return;
    assignForId.value = null;
    assignError.value = '';
    offerPurchase.value = false;
}

function openRemove(member: EmployeeFamilyMember) {
    if (familyMutating.value) return;
    assignForId.value = null;
    assignError.value = '';
    offerPurchase.value = false;
    removeError.value = '';
    removeForId.value = member.id;
}

function closeRemove() {
    // Same as closeAssign: the success path clears `removeForId` itself.
    if (familyMutating.value) return;
    removeForId.value = null;
    removeError.value = '';
}

/**
 * Put a family member in one of the company's own licenses.
 *
 * The row is written from the server's answer and from nothing else. A gated
 * control that flips on the click and keeps the flip through a refusal has hit
 * this app twice (#1786), and what it would lie about here is who is paying for
 * whom — so a refusal leaves the row showing the license the company actually
 * holds and says why underneath it.
 */
async function assignFamilyLicense(member: EmployeeFamilyMember, license: string) {
    if (!companyId.value || !employeeId.value || !canEditEmployee.value) return;
    // The layer under the disabled controls, so a path that slips past them
    // cannot start a second mutation against the same pool.
    if (familyMutating.value) return;
    assigningRelativeId.value = member.id;
    assignError.value = '';
    offerPurchase.value = false;
    // Set before the request, not after it answers. The drawer's close button and
    // backdrop are not part of the family lock — nor should they be, a dead X with
    // no explanation being its own defect — so an admin can close over an in-flight
    // PUT, and `closeDrawer` hands the page `{ updated: <this flag> }`. False there
    // skips `refreshRosterAndLicenses`, leaving the roster and the availability
    // cards on the counts from before a license that did leave the pool. The
    // parent's refresh is a read, so asking for one after a refusal costs two
    // reads and tells the truth either way.
    hasUpdatedDuringSession.value = true;
    try {
        const assigned = await businessService.assignEmployeeFamilyMemberLicense(
            companyId.value,
            employeeId.value,
            member.id,
            license,
        );
        // The server's slug, not the one that was clicked: a tier change claims
        // first and releases second, so what lands is its answer to give.
        familyMembers.value = familyMembers.value.map(
            (m) => (m.id === assigned.id ? { ...m, license: assigned.license } : m),
        );
        hasUpdatedDuringSession.value = true;
        assignForId.value = null;
        showSuccess('License assigned', `${personDisplayName(member)} now holds a company license.`);
        // One license left the pool: the panel's own figures, the employee's tier
        // list and the roster's availability cards all read the same counts.
        void refreshSeatCounts();
    } catch (error: unknown) {
        // A 404 is a stale view, not something to try again. Every 404 this endpoint
        // gives means the row that was clicked is not there to assign to any more:
        // the relative has left this employee's family, or the employee has left the
        // company (`employee_not_found`). "Please try again" over a row that will
        // never come back is the wrong instruction, so the panel closes, the list is
        // re-read, and the toast says what changed underneath them.
        if (error instanceof ApiError && error.code === 404) {
            assignForId.value = null;
            // Branch on the re-read, the same way the removal's 404 does: a GET that
            // did not land leaves the stale row on screen, so claiming "the list has
            // been refreshed" over it would be a toast contradicting what the admin
            // can see — and inviting a retry against a row that is already gone.
            const read = await fetchFamily();
            showError(
                'Out of date',
                refusalWords(error, ASSIGN_REFUSALS, read
                    ? `${personDisplayName(member)} is no longer in this employee's family, so no license was assigned. The list has been refreshed.`
                    : `No license was assigned, and this list could not be re-read just now. Close and reopen the drawer to see where ${personDisplayName(member)} stands.`),
            );
            return;
        }
        assignError.value = refusalWords(error, ASSIGN_REFUSALS, 'Could not assign that license. Please try again.');
        // The one refusal with a way forward, and the way forward is the ordinary
        // business checkout (PB-013) — never a family-specific purchase.
        offerPurchase.value = error instanceof ApiError && error.reason === 'no_license_available';
    } finally {
        assigningRelativeId.value = null;
    }
}

/**
 * What a removal settles, whether the server cut the link on this request or had already cut it.
 * The local drop happens before the re-read so a transient GET failure leaves the rest of the
 * family on screen rather than nothing.
 *
 * `resolveRemove404` reaches this having already re-read, so it pays for one extra GET to keep
 * a single definition of what a removal settles. A rare branch; not worth two.
 */
async function settleRemoval(member: EmployeeFamilyMember) {
    removeForId.value = null;
    familyMembers.value = familyMembers.value.filter((m) => m.id !== member.id);
    await Promise.all([fetchFamily(), refreshSeatCounts()]);
    showSuccess(
        'Family updated',
        `${personDisplayName(member)} is no longer in this employee's family. Their account is untouched.`,
    );
}

/**
 * A 404 from the remove endpoint, decided rather than assumed.
 *
 * Two different things answer 404 here and the status alone cannot tell them
 * apart. `familyMemberOf` answers it when the link is already cut — before the
 * lock or re-proved under it — which is this request's own outcome achieved. But
 * `holdForFamilyLicenseWrite` answers it too, when the company no longer employs
 * the employee, and it throws as the FIRST statement inside the transaction, so
 * the whole removal rolls back: no license released, no contact row deleted.
 * Reading both as "already done" dropped the row and showed a green "their
 * account is untouched" while the relative still held the company license and
 * still counted against the company's pool.
 *
 * So the LIST is the arbiter, never the status: only a relative the server no
 * longer lists is gone. A re-read that does not land proves nothing, and nothing
 * is what it is allowed to claim. The server also names the second case
 * (`employee_not_found`, via `RendersInTheCallersApi`), which is what words it —
 * but the proof does not depend on that reason arriving, because an installed
 * build outlives the API it shipped against.
 */
async function resolveRemove404(member: EmployeeFamilyMember, error: ApiError) {
    const read = await fetchFamily();
    if (read && !familyMembers.value.some((m) => m.id === member.id)) {
        await settleRemoval(member);
        return;
    }
    removeError.value = refusalWords(
        error,
        REMOVE_REFUSALS,
        read
            ? `Nothing was removed. ${personDisplayName(member)} is still in this employee's family. Please try again.`
            : 'Whether this family member was removed could not be confirmed just now. Reopen the drawer to check.',
    );
}

/**
 * The company lets go of a family member (PB-007). The license goes back to the
 * pool, the family link is cut, the person keeps everything.
 *
 * The list is written from the re-read, not from the click: the person is still
 * an account, and what changed is the company's claim on them, which only the
 * endpoint can report. The one exception is the row that was just removed — the
 * 204 is proof its link is cut — and dropping it before the re-read is what
 * keeps a re-read that does not land from reading as an empty family. A refusal
 * — a relative who is an employee in their own right — leaves the row with the
 * license and the link it still has.
 */
async function removeFamilyMember(member: EmployeeFamilyMember) {
    if (!companyId.value || !employeeId.value || !canEditEmployee.value) return;
    // The layer under the disabled controls — see assignFamilyLicense.
    if (familyMutating.value) return;
    removingRelativeId.value = member.id;
    removeError.value = '';
    // Before the request — see assignFamilyLicense: a drawer closed over an
    // in-flight DELETE must still ask the page to re-read its counts.
    hasUpdatedDuringSession.value = true;
    try {
        await businessService.removeEmployeeFamilyMember(companyId.value, employeeId.value, member.id);
        await settleRemoval(member);
    } catch (error: unknown) {
        // A 404 is two different outcomes wearing one status, so it is resolved
        // against the list rather than assumed ({@see resolveRemove404}).
        if (error instanceof ApiError && error.code === 404) {
            await resolveRemove404(member, error);
            return;
        }
        removeError.value = refusalWords(error, REMOVE_REFUSALS, 'Could not remove this family member. Please try again.');
    } finally {
        removingRelativeId.value = null;
    }
}

/**
 * Whether a family member's own record can be opened. `license` on the family
 * payload is the slug of the license THIS COMPANY controls on them, or null, and
 * the policy arm that opens a non-employee's record to a company admin is the
 * one that reads it — so a relative with no company license is refused, and the
 * control is disabled rather than failing on the click.
 */
const canImpersonateRelative = (member: EmployeeFamilyMember) =>
    canImpersonateTarget({ isBusinessEmployee: false, licensed: !!member.license });

/**
 * Open a family member's account the way the console opens anyone's: the person
 * store navigates to `/dashboard?person=<id>` and the address is what makes the
 * switch. Same call as the Impersonate control in the header — no second
 * mechanism, and no `?person` built by hand here.
 */
async function impersonateRelative(member: EmployeeFamilyMember) {
    if (!canEditEmployee.value || impersonatingRelativeId.value !== null) return;
    if (!canImpersonateRelative(member)) return;
    impersonatingRelativeId.value = member.id;
    try {
        // The drawer closes only on a switch that took: after a failure the admin
        // is still here, with the store's own word on screen.
        if (await usePersonStore().startImpersonation(member.id)) {
            closeDrawer();
        }
    } finally {
        impersonatingRelativeId.value = null;
    }
}

/**
 * The one money path (PB-013): the ordinary business checkout, buying company
 * licenses, which are then assigned here. There is deliberately no
 * family-specific plan, quantity or price in this drawer.
 *
 * New tab, demo refusal and the refresh affordance are all `onLicenseChange`'s,
 * for its reasons — navigating in place would close this drawer over edits that
 * have not autosaved yet.
 */
function buyCompanyLicenses() {
    if (isDemoMode()) {
        showError('Not in the demo', 'Buying licenses opens the real checkout, which the demo cannot reach.');
        return;
    }
    if (!canBuySeats.value || !companyId.value) return;
    window.open(
        checkoutBusinessUrl(companyId.value, `/business/${companySlug.value ?? ''}/employees`),
        '_blank',
        'noopener',
    );
    awaitingSeats.value = true;
}

async function fetchReports() {
    if (!canViewReports.value) {
        recentReports.value = [];
        reportsLoading.value = false;
        return;
    }
    if (!companyId.value || !employeeId.value) {
        reportsLoading.value = false;
        return;
    }
    reportsLoading.value = true;
    try {
        const response = await businessService.getEmployeeReports(
            companyId.value,
            employeeId.value,
            { per_page: 10 }
        );
        recentReports.value = response.data || [];
    } catch (error) {
        console.error('Failed to load reports:', error);
        recentReports.value = [];
    } finally {
        reportsLoading.value = false;
    }
}

async function saveChanges(): Promise<boolean> {
    // Re-entrancy guard: a save is already in flight (debounced autosave or
    // backdrop close). Bail rather than stacking a duplicate write — the
    // employee/hasChanges state only settles after the awaited request below.
    if (isSaving.value) return false;
    if (!companyId.value || !employee.value || !hasChanges.value) return false;
    if (!canEditEmployee.value) return false;

    // PrimeVue 4 Select doesn't reliably bind an empty-string `optionValue`
    // (the "No license" option's slug) — when picked, v-model can end up
    // with the displayName ("No license") instead of the slug (""). Resolve
    // the actual option object by either field so we always get the real
    // slug at the API boundary.
    const chosenOption = licenseOptions.value.find(
        (opt) => opt.slug === selectedLicense.value || opt.displayName === selectedLicense.value
    );
    const chosenSlug = chosenOption?.slug ?? selectedLicense.value ?? '';

    // Final-line guard: even if a click slips past `onLicenseChange` or
    // `optionDisabled`, refuse to ship an assignment that exceeds the
    // company's purchased pool. Re-saving the employee's existing license is
    // fine — that's a no-op assignment-wise.
    if (chosenSlug !== (employee.value.license || '')) {
        if (chosenOption?.outOfStock) {
            showError(
                'Out of stock',
                `Your company has 0 ${chosenOption.name} licenses available. Purchase more to assign.`,
            );
            return false;
        }
    }

    isSaving.value = true;
    saveErrors.value = {};
    try {
        // Snapshot the exact values being persisted. Reads taken after the
        // await below could pick up edits made mid-save, which would let the
        // reconciliation write a value that was never sent and hide the newer
        // edit from `hasChanges`.
        const held = employee.value;
        const heldRoles = [...(held.roles || [])].filter((r) => r !== 'Employee');
        const persistedDepartment = selectedDepartment.value ?? null;
        const persistedRoles = [...selectedRoles.value];

        // One write per changed control, kept apart so a refusal names its control.
        const writes: Array<{ field: SaveField; run: () => Promise<void> }> = [];
        if (chosenSlug !== (held.license || '')) {
            // The empty slug is "No license": the service sends it as
            // `NO_LICENSE_SLUG`, which detaches person_id from the existing
            // PersonLicense (no row deleted, the license stays in the company's pool).
            writes.push({
                field: 'license',
                run: () => businessService.updateEmployeeLicense(companyId.value!, held.id, chosenSlug || null),
            });
        }
        if (persistedDepartment !== (held.department_id ?? null)) {
            // Pass `null` through when the department was cleared so the
            // backend detaches the assignment.
            writes.push({
                field: 'department',
                run: () => businessService.updateEmployeeDepartment(companyId.value!, held.id, persistedDepartment),
            });
        }
        if (canEditRoles.value && JSON.stringify([...persistedRoles].sort()) !== JSON.stringify([...heldRoles].sort())) {
            writes.push({
                field: 'roles',
                run: () => businessService.updateEmployeeRoles(companyId.value!, held.id, persistedRoles),
            });
        }
        const results = await Promise.allSettled(writes.map((w) => w.run()));
        const refused: Partial<Record<SaveField, string>> = {};
        results.forEach((result, i) => {
            if (result.status === 'rejected') {
                refused[writes[i].field] = result.reason?.message || 'Failed to save changes';
            }
        });

        // Normalize the license v-model to the persisted slug (`chosenSlug`),
        // not the raw v-model value: PrimeSelect can bind the display label
        // ("No license") for the empty-slug option, so persisting it would make
        // the next save see a fake license diff and fire an unnecessary
        // `updateEmployeeLicense(null)`. Skip the reset when the picker moved to
        // a different license mid-save so that newer choice isn't clobbered.
        const resolvedLicenseSlug = licenseOptions.value.find(
            (opt) => opt.slug === selectedLicense.value || opt.displayName === selectedLicense.value
        )?.slug ?? selectedLicense.value ?? '';
        // A refused control goes back to what the employee actually holds (D9): a
        // control left on the refused value reads as saved when it was not. Only
        // when it still shows the value this save sent, though — an edit made
        // while the request was in flight is newer than the refusal and stays,
        // so the re-armed autosave can send it.
        const sameRoles = (a: string[], b: string[]) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
        if (refused.license) {
            if (resolvedLicenseSlug === chosenSlug) selectedLicense.value = held.license || '';
        } else if (resolvedLicenseSlug === chosenSlug) {
            selectedLicense.value = chosenSlug;
        }
        if (refused.department && (selectedDepartment.value ?? null) === persistedDepartment) {
            selectedDepartment.value = held.department_id ?? null;
        }
        if (refused.roles && sameRoles(selectedRoles.value, persistedRoles)) selectedRoles.value = heldRoles;
        // Reconcile against exactly what was persisted so an edit made during
        // the in-flight request stays visible to `hasChanges` and is saved by
        // the follow-up autosave; a refused field keeps its held value.
        employee.value = {
            ...held,
            license: refused.license ? held.license : chosenSlug || undefined,
            department_id: refused.department ? held.department_id : persistedDepartment ?? undefined,
            roles: refused.roles ? held.roles : persistedRoles,
        };
        saveErrors.value = refused;

        const refusedCount = Object.keys(refused).length;
        if (writes.length > refusedCount) {
            hasUpdatedDuringSession.value = true;
            // One refresh covers both sides of the write: the roster row the reader
            // came from, and — because a license assignment/detach shifts the
            // company's available pool — the "N available" counts this dropdown and
            // the page's availability cards both read from. Not awaited: the save is
            // already reported, and the counts land under the reader.
            void refreshFromPage();
        }
        if (refusedCount > 0) {
            showError('Error', Object.values(refused)[0]!);
            return false;
        }
        showSuccess('Saved', 'Changes saved automatically');
        return true;
    } finally {
        isSaving.value = false;
    }
}

function closeDrawer() {
    dialogRef.value?.close({ updated: hasUpdatedDuringSession.value });
}

// Send invite (D29): a person added without an invite, who has never signed in,
// gets the welcome email from here as well as from the never-signed-in view. The
// same endpoint serves a resend, but a resend is the roster's and Pending's job.
const isSendingInvite = ref(false);
const inviteStatus = ref<{ text: string; error: boolean } | null>(null);
// Same reading as the roster: invite_state when the API sends it, else the old boolean.
const inviteState = computed(() =>
    employee.value?.invite_state ?? (employee.value?.invited ? 'invited' : 'not_invited'),
);
// Offered when nothing was sent, or when what was sent no longer works.
const canSendInvite = computed(() =>
    !!employee.value && canEditEmployee.value && !employee.value.last_login_at
    && (inviteState.value === 'not_invited' || inviteState.value === 'invite_stale'),
);

async function sendInvite(): Promise<void> {
    if (!companyId.value || !employee.value || !canSendInvite.value) return;
    isSendingInvite.value = true;
    inviteStatus.value = null;
    try {
        await businessService.sendEmployeeInvite(companyId.value, employee.value.id);
        // The API records the invite before it answers, so the badge can follow now,
        // and the roster re-reads on close so its row says Invited too.
        employee.value.invited = true;
        employee.value.invite_state = 'invited';
        hasUpdatedDuringSession.value = true;
        inviteStatus.value = { text: `Invite sent to ${primaryEmail.value || 'this employee'}.`, error: false };
    } catch (error: any) {
        inviteStatus.value = { text: error?.message || 'Failed to send invite', error: true };
    } finally {
        isSendingInvite.value = false;
    }
}

// Flush any pending autosave before closing so a fast close within the
// debounce window doesn't drop the last edit.
async function handleBackdropClick() {
    if (autosaveTimer) {
        clearTimeout(autosaveTimer);
        autosaveTimer = null;
    }
    if (isSaving.value && savePromise) {
        // A debounced autosave is already persisting these edits — await it
        // instead of firing a duplicate write.
        await savePromise;
    }
    // Flush any edit made during (or after) that in-flight save so a fast close
    // doesn't drop the last change. saveChanges no-ops when there is nothing to
    // persist, so this is safe when the awaited save already covered everything.
    if (hasChanges.value && canEditEmployee.value) {
        await saveChanges();
    }
    closeDrawer();
}

// Debounced autosave — role/department/license edits persist as they happen.
function scheduleAutosave() {
    if (!isReady.value || !canEditEmployee.value) return;
    if (autosaveTimer) clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(() => {
        autosaveTimer = null;
        if (!hasChanges.value) return;
        // A previous autosave is still persisting — re-arm instead of dropping
        // this edit, so a change made mid-save is saved once the request
        // settles rather than silently lost.
        if (isSaving.value) {
            scheduleAutosave();
            return;
        }
        // Track the in-flight save so a concurrent backdrop close awaits it
        // instead of starting a second write.
        savePromise = saveChanges();
    }, 600);
}

watch([selectedLicense, selectedDepartment, selectedRoles], scheduleAutosave, { deep: true });

const isImpersonating = ref(false);

// Rule K3 (target eligibility): this drawer's subject is a company employee, so
// it is always an eligible impersonation target regardless of license — the
// employer relationship authorizes the PII handoff. `licensed` is derived so the
// same central predicate stays correct if the trigger ever surfaces a
// non-employee. The caller still needs `write employees` (canEditEmployee).
const impersonationEligible = computed(() =>
    canImpersonateTarget({
        isBusinessEmployee: !!employee.value && !!companyId.value,
        licensed: !!currentLicenseSlug.value,
    })
);

async function impersonate() {
    if (!employee.value || isImpersonating.value) return;
    if (!canEditEmployee.value) return;
    if (!impersonationEligible.value) return;
    isImpersonating.value = true;
    try {
        // The store runs the whole switch, loader, landing on the employee's dashboard and the word on a failure
        // included. The drawer closes only on a switch that took: after a failure the member is still here.
        if (await usePersonStore().startImpersonation(employee.value.id)) {
            closeDrawer();
        }
    } finally {
        isImpersonating.value = false;
    }
}

const downloadingReportId = ref<string | null>(null);

async function downloadReport(report: Report) {
    if (!companyId.value) return;
    downloadingReportId.value = report.id;
    try {
        // Anchor to a URL S3 signs with Content-Disposition: attachment. Reading
        // the object from script would be a cross-origin fetch against a bucket
        // whose CORS policy this application does not own. Mirrors downloadReport
        // in pages/business/[slug]/reports/index.vue.
        const url = await businessService.getReportUrl(companyId.value, report.id, 'attachment');

        const a = document.createElement('a');
        a.href = url;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
    } catch (error) {
        console.error('Failed to download report:', error);
        showError('Download failed', 'Could not download the report. Please try again.');
    } finally {
        downloadingReportId.value = null;
    }
}

// Retained so the company-switch watcher below can close it directly — this
// dialog mounts on the layout-level DynamicDialog host, outside any page's
// openDialogs tracking.
let familyDialog: DynamicDialogInstance | undefined;

function openAddFamilyMember() {
    if (!companyId.value || !employee.value) return;
    familyDialog = useDynamicDialog(ModalNames.ADD_EMPLOYEE_FAMILY_MEMBER, {
        companyId: companyId.value,
        employeeId: employee.value.id,
        employeeName: employeeName.value,
    }, {
        pt: {
            mask: 'bg-[#09090b80] modal-main flex items-center justify-center',
            root: 'max-w-xl w-full mx-auto',
            header: '!hidden !p-0 !m-0 !h-0 !min-h-0 !border-0 overflow-hidden',
            content: 'p-0',
        },
        onClose: async (event: any) => {
            // Re-fetch the family list so the newly added adult appears
            // immediately; the consent chip is unaffected by this flow,
            // but fetchEmployee keeps any other employee state in sync.
            await Promise.all([fetchEmployee(), fetchFamily()]);
            if (event?.data?.created) {
                hasUpdatedDuringSession.value = true;
                showSuccess('Family updated', 'Family member has been added.');
            }
        },
    });
}

// Retained so the company-switch watcher below can close it directly — same
// untracked, layout-level-host reasoning as familyDialog above.
let childDialog: DynamicDialogInstance | undefined;

function openAddChild() {
    if (!companyId.value || !employee.value) return;
    childDialog = useDynamicDialog(ModalNames.ADD_EMPLOYEE_CHILD, {
        companyId: companyId.value,
        employeeId: employee.value.id,
        employeeName: employeeName.value,
        consentSigned: !!employee.value.minor_consent_signed,
    }, {
        pt: {
            mask: 'bg-[#09090b80] modal-main flex items-center justify-center',
            root: 'max-w-xl w-full mx-auto',
            header: '!hidden !p-0 !m-0 !h-0 !min-h-0 !border-0 overflow-hidden',
            content: 'p-0',
        },
        onClose: async (event: any) => {
            // Re-fetch the employee unconditionally so the consent chip stays
            // in sync after Send Consent Email / external signing. Re-fetch
            // the family list too so a newly added child appears immediately.
            await Promise.all([fetchEmployee(), fetchFamily()]);
            if (event?.data?.created) {
                hasUpdatedDuringSession.value = true;
                showSuccess('Family updated', 'Child has been added.');
            }
        },
    });
}

// Retained so the company-switch watcher below can close it directly — same
// untracked, layout-level-host reasoning as familyDialog above.
let removeDialog: DynamicDialogInstance | undefined;

function confirmRemoveEmployee() {
    if (!companyId.value || !employee.value) return;
    if (!canEditEmployee.value) return;
    removeDialog = useDynamicDialog(ModalNames.REMOVE_EMPLOYEE, {
        companyId: companyId.value,
        employeeId: employee.value.id,
        employeeName: employeeName.value,
    }, {
        pt: {
            mask: 'bg-[#09090b80] modal-main flex items-center justify-center',
            root: 'bg-white rounded-xl shadow-xl max-w-md w-full mx-auto overflow-hidden border-0',
            header: '!hidden !p-0 !m-0 !h-0 !min-h-0 !border-0 overflow-hidden',
            content: 'p-0',
        },
        onClose: (event: any) => {
            if (event?.data?.removed) {
                // Close the drawer with `removed: true` (not the
                // default `updated` payload that `closeDrawer()` sends)
                // so the parent table can refresh after a deletion. The
                // first `close(...)` call wins, so calling `closeDrawer()`
                // first would have hidden the `removed` flag.
                dialogRef.value?.close({ removed: true });
            }
        },
    });
}

// The employees page closes this drawer when it unmounts, but on a company
// switch the store moves first: the business middleware's fetchBusiness for the
// next company lands while the page is still up. So watch the store directly:
// close the nested family/child/remove dialogs and this drawer itself the
// moment the live company diverges from openingCompanyId, before any of them
// can submit against company A while the portal already shows company B.
watch(() => businessStore.companyId, (id) => {
    if (id !== openingCompanyId) {
        familyDialog?.close();
        childDialog?.close();
        removeDialog?.close();
        closeDrawer();
    }
});

// The nested dialogs are hosted at layout level, independent of this drawer's
// subtree, so whatever closes the drawer must close them too — its own close
// control as much as the employees page unmounting. Left open, they would
// outlive the drawer and could still submit their captured company/employee ids.
onBeforeUnmount(() => {
    familyDialog?.close();
    childDialog?.close();
    removeDialog?.close();
});

// `immediate: true` runs the initial fetches when the drawer opens, so
// no separate `onMounted` is needed. Two batches doubled API traffic and
// could resolve out of order.
watch([companyId, employeeId], () => {
    if (companyId.value && employeeId.value) {
        fetchEmployee();
        fetchReports();
        fetchFamily();
    }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.employee-drawer {
    @apply bg-white w-full sm:w-[480px] h-screen flex flex-col overflow-y-auto right-0 fixed top-0;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.08);
}

.drawer-header {
    @apply flex justify-between items-start px-6 py-5 border-b border-zinc-200 flex-shrink-0;

    .header-content {
        @apply flex-1 min-w-0 pr-4;

        .employee-name {
            @apply text-xl font-bold text-zinc-900 m-0 mb-1 truncate;
        }

        .employee-email {
            @apply text-sm text-zinc-500 m-0 flex items-center gap-2 min-w-0;

            .employee-email__value {
                @apply truncate;
            }
        }

        .employee-email--company {
            @apply mt-1;
        }

        .invite-note {
            @apply m-0 mt-1 text-xs text-zinc-500;

            &--error {
                @apply text-red-600;
            }
        }

        .email-tag {
            @apply inline-flex items-center flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-zinc-100 text-zinc-500;
        }
    }

    .header-actions {
        @apply flex items-center gap-2 flex-shrink-0;

        .close-btn {
            @apply w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors;
        }
    }
}

.drawer-loading {
    @apply p-6;
}

.drawer-body {
    @apply flex-1 overflow-y-auto px-6 py-6;
}

.drawer-section {
    @apply mb-8;

    .section-title {
        @apply text-xs font-semibold text-zinc-500 uppercase tracking-wider m-0 mb-3;
    }

    .section-row {
        @apply flex items-center justify-between gap-3 mb-3;

        .section-title {
            @apply mb-0;
        }
    }

    .form-group {
        @apply mb-4;

        .form-label {
            @apply block text-sm font-medium text-zinc-700 mb-2;
        }

        .refresh-seats {
            @apply block mt-2 text-xs font-semibold text-blue-600 hover:underline;
        }
    }

    .restricted-field {
        @apply inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-100 text-zinc-400 text-sm;
    }

    .form-error {
        @apply block text-xs text-red-600 mt-1;
    }

    .personal-license {
        @apply flex items-start gap-2 mt-3 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200;

        .personal-license__icon {
            @apply text-amber-500 flex-shrink-0 mt-0.5;
        }

        .personal-license__body {
            @apply min-w-0 m-0 text-sm text-zinc-800;
        }
    }
}

.risk-score-row {
    @apply flex items-center gap-3 mb-2;

    .risk-value {
        @apply text-lg font-bold text-zinc-900;
    }

    .risk-bar {
        @apply flex-1 h-2 bg-zinc-200 rounded-full overflow-hidden;

        .risk-bar-fill {
            @apply h-full rounded-full transition-all duration-300;
        }
    }
}

.lpoa-status {
    @apply text-sm text-zinc-600 m-0;

    &--unsigned {
        @apply text-amber-700;
    }
}

.risk-label {
    @apply text-sm text-zinc-600 m-0;

    &.risk-high {
        @apply text-red-600;
    }

    &.risk-medium {
        @apply text-amber-600;
    }

    &.risk-low {
        @apply text-green-600;
    }
}

.dept-value,
.dept-option {
    @apply flex items-center gap-2;

    .dept-indicator {
        @apply w-2.5 h-2.5 rounded-full flex-shrink-0;
    }
}

.placeholder-text {
    @apply text-zinc-400;
}

// PB-136. Everything here is scoped under `.license-select` / `.license-value`
// so the Department and Roles selectors above keep PrimeVue's own muted chevron.
.license-value {
    @apply font-semibold text-zinc-900;
}

.license-select {
    // The chevron stays — only its weight changes. PrimeVue's ChevronDownIcon is a
    // fill-only SVG path, so `font-weight` does nothing to it; inking it to
    // `text-zinc-900` (which is `--ink`) and stroking the path in the same
    // `currentColor` is what actually reads as black and bold.
    :deep(.p-select-dropdown-icon) {
        @apply text-zinc-900;
        stroke: currentColor;
        stroke-width: 0.75;
    }
}

.license-option {
    @apply flex items-center gap-2;

    &--out {
        @apply opacity-60;
    }
}

.license-avail {
    @apply text-xs text-green-600 ml-auto;

    &--out {
        @apply text-amber-600;
    }
}

.reports-list {
    @apply space-y-2;

    .report-card {
        @apply flex items-center justify-between p-3 rounded-lg border border-zinc-200 bg-zinc-50/50;

        .report-info {
            @apply flex flex-col;

            .report-name {
                @apply text-sm font-medium text-zinc-900;
            }

            .report-date {
                @apply text-xs text-zinc-500;
            }
        }

        .download-btn {
            @apply inline-flex items-center justify-center w-9 h-9 rounded-full bg-transparent border-0 text-zinc-600 cursor-pointer transition-colors;

            &:hover:not(:disabled) {
                @apply bg-zinc-100 text-zinc-900;
            }

            &:disabled {
                @apply opacity-50 cursor-not-allowed;
            }
        }
    }
}

.reports-loading,
.reports-empty {
    @apply py-4;
}

.drawer-footer {
    @apply pt-6 mt-6 border-t border-zinc-200;

    .remove-link {
        @apply text-sm text-red-600 hover:text-red-700 hover:underline cursor-pointer;
    }
}

.consent-chip {
    @apply inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border;

    &--signed {
        @apply bg-green-50 text-green-700 border-green-200;
    }

    &--unsigned {
        @apply bg-amber-50 text-amber-700 border-amber-200;
    }
}

.family-hint {
    @apply text-xs text-zinc-500 m-0 mb-3 leading-relaxed;
}

/* The family read failed. Not the muted hint's grey: this one is a problem with
   what is on screen, not a description of it. */
.family-note {
    @apply text-xs text-amber-700 m-0 mt-2 mb-1 leading-relaxed;
}

.family-loading {
    @apply mb-2;
}

.family-actions {
    @apply flex flex-wrap gap-2 mt-3;
}

.family-list {
    @apply flex flex-col gap-2 mb-1;

    .family-card {
        @apply flex flex-col gap-2 px-3 py-2 rounded-lg border border-zinc-200 bg-zinc-50/60;

        &__head {
            @apply flex items-center justify-between gap-3;
        }

        &__info {
            @apply flex flex-col min-w-0 mr-auto;
        }

        &__name {
            @apply text-sm font-medium text-zinc-900 truncate;
        }

        &__meta {
            @apply text-xs text-zinc-500 truncate;
        }

        &__unlicensed {
            @apply text-xs text-zinc-500 flex-shrink-0;
        }

        /* What it takes to open this relative's account, where the control would
           otherwise have sat disabled with its reason only in a tooltip. */
        &__requirement {
            @apply text-xs text-zinc-500 leading-relaxed;
        }

        &__tag {
            @apply inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0;
        }

        &__actions {
            @apply flex flex-wrap items-center gap-3;
        }
    }
}

.family-link {
    @apply text-xs font-medium text-zinc-700 underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer;

    &:hover:not(:disabled) {
        @apply text-zinc-900;
    }

    &:disabled {
        @apply text-zinc-400 no-underline cursor-not-allowed;
    }

    &--danger {
        @apply text-red-600;

        &:hover:not(:disabled) {
            @apply text-red-700;
        }
    }
}

/* The assign and remove panels: they say what the act does before it happens,
   inside the row it acts on, so nothing is read out of context. */
.family-panel {
    @apply flex flex-col gap-2 pt-2 border-t border-zinc-200;

    &__lede {
        @apply text-xs text-zinc-600 m-0 leading-relaxed;
    }

    &__tiers {
        @apply flex flex-wrap gap-2;
    }

    &__error {
        @apply text-xs text-red-600 m-0;
    }

    &__aside {
        @apply text-xs text-zinc-500;
    }

    &__actions {
        @apply flex flex-wrap items-center gap-3;
    }
}

.family-tier {
    @apply text-xs font-medium text-zinc-800 px-2.5 py-1 rounded-md border border-zinc-300 bg-white cursor-pointer;

    &:hover:not(:disabled) {
        @apply border-zinc-400 bg-zinc-50;
    }

    &:disabled {
        @apply text-zinc-400 cursor-not-allowed;
    }
}

.drawer-backdrop {
    @apply fixed inset-0 z-50 bg-black/30;
}

.slide-in-right-enter-active,
.slide-in-right-leave-active {
    transition: transform 0.3s ease;
}

.slide-in-right-enter-from,
.slide-in-right-leave-to {
    transform: translateX(100%);
}
</style>
